import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSession } from './useSession';
import { apiClient } from '../lib/api-client';
import { authenticatePasskey, encodeAuthenticationResponse } from '@wafra/passkey';
import type { Session, User } from './useSession';

const AUTH_TOKEN_KEY = 'auth_token';

export function updateSessionToken(token: string) {
  if (typeof window !== 'undefined') {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }
}

export function getSessionToken() {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }
  return null;
}

export function clearSessionToken() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
}

export function useAuth() {
  const { setSession, clearSession } = useSession();
  
  // Development hack: Get phone from URL parameters
  const getPhoneFromUrl = useCallback(() => {
    if (typeof window === 'undefined') return undefined;
    const urlParams = new URLSearchParams(window.location.search);
    const phone = urlParams.get('phone');
    if (!phone) return undefined;
    
    // Clean and normalize the phone number
    const cleanPhone = phone.trim();
    return cleanPhone.startsWith('+') ? cleanPhone : `+${cleanPhone}`;
  }, []);

  // Remember last user who signed in
  const getLastUserPhone = useCallback(() => {
    if (typeof window === 'undefined') return undefined;
    return localStorage.getItem('lastUserPhone') || undefined;
  }, []);

  const setLastUserPhone = useCallback((phone: string) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('lastUserPhone', phone);
  }, []);

  // React Query mutations
  const signInMutation = useMutation({
    mutationFn: (credentials: { phone: string; password: string }) => 
      apiClient.post<Session>('auth.signIn', credentials)
  });
  
  const signUpMutation = useMutation({
    mutationFn: (userData: { phone: string; password: string; country: string }) => 
      apiClient.post<Session>('auth.signUp', userData)
  });
  
  const verifyCredentialsMutation = useMutation({
    mutationFn: (credentials: { phone: string; password: string }) => 
      apiClient.post<Session>('auth.verifyCredentials', credentials)
  });
  
  const refreshMutation = useMutation({
    mutationFn: () => apiClient.post<Session>('auth.refresh')
  });

  // Passkey authentication mutations
  const generateAuthenticationChallengeMutation = useMutation({
    mutationFn: (phone?: string) => apiClient.post<{
      challenge: {
        challenge: string,
        rpId: string,
        allowCredentials: { id: string; type: string }[],
        userVerification: UserVerificationRequirement,
      },
      challengeId: string,
    }>('passkey.generateAuthenticationChallenge', phone ? { phone } : {})
  });
  
  const verifyAuthenticationMutation = useMutation({
    mutationFn: (data: { challengeId: string; credential: string }) => apiClient.post<{
      user: User,
      hasApprovedPasskey: boolean,
      passkeyId: string,
      token: string,
    }>('passkey.verifyAuthentication', data)
  });

  const login = useCallback(
    async (credentials: { phone: string; password: string }) => {
      try {
        const result = await signInMutation.mutateAsync(credentials);

        setSession({
          user: result.user,
          hasApprovedPasskey: result.hasApprovedPasskey,
          passkeyId: result.passkeyId,
        });

        return result;
      } catch (error: any) {
        throw new Error(error.message || 'Login failed');
      }
    },
    [signInMutation, setSession]
  );

  const register = useCallback(
    async (userData: { phone: string; password: string; country: string }) => {
      try {
        const result = await signUpMutation.mutateAsync(userData);

        setSession({
          user: result.user,
          hasApprovedPasskey: result.hasApprovedPasskey,
          passkeyId: result.passkeyId,
        });

        return result;
      } catch (error: any) {
        throw new Error(error.message || 'Registration failed');
      }
    },
    [signUpMutation, setSession]
  );

  // Verify credentials without changing the session
  const verifyCredentials = useCallback(
    async (credentials: { phone: string; password: string }) => {
      try {
        await verifyCredentialsMutation.mutateAsync(credentials);
        return true;
      } catch (error: any) {
        return false;
      }
    },
    [verifyCredentialsMutation]
  );

  const logout = useCallback(async () => {
    await clearSession();
  }, [clearSession]);

  const refreshSession = useCallback(async () => {
    const token = getSessionToken();
    if (token) {
      try {
        const result = await refreshMutation.mutateAsync();

        setSession({
          user: result.user,
          hasApprovedPasskey: result.hasApprovedPasskey,
          passkeyId: result.passkeyId,
        });
      } catch (error) {
        console.error('Session refresh failed:', error);
        await clearSession();
      }
    }
  }, [refreshMutation, setSession, clearSession]);

  /**
   * Login with passkey using tRPC authentication flow
   */
  const loginWithPasskey = useCallback(async (phone?: string) => {
    try {
      await clearSession();

      // Get phone from parameter, URL, or last user
      const phoneToUse = phone || getPhoneFromUrl() || getLastUserPhone();

      // Step 1: Generate authentication challenge
      const challengeResponse = await generateAuthenticationChallengeMutation.mutateAsync(phoneToUse);
      const { challenge: challengeData, challengeId } = challengeResponse;

      // Step 2: Use passkey package to authenticate
      const authResult = await authenticatePasskey({
        challenge: challengeData.challenge,
        rpID: challengeData.rpId,
        allowCredentials: challengeData.allowCredentials,
        userVerification: challengeData.userVerification,
      });

      // Step 3: Encode the raw credential for JSON serialization and send to server
      const encodedCredential = encodeAuthenticationResponse(authResult.rawCredential);
      const result = await verifyAuthenticationMutation.mutateAsync({
        challengeId,
        credential: encodedCredential,
      });

      // Remember the user's phone for next time
      if (result.user.phone) {
        setLastUserPhone(result.user.phone);
      }

      // Store the token in localStorage
      if (result.token) {
        console.log('loginWithPasskey: Storing token in localStorage');
        localStorage.setItem(AUTH_TOKEN_KEY, result.token);
      } else {
        console.log('loginWithPasskey: No token received from server');
      }

      console.log('loginWithPasskey: Setting session with user:', result.user.phone);
      setSession({
        user: result.user,
        hasApprovedPasskey: result.hasApprovedPasskey,
        passkeyId: result.passkeyId,
        token: result.token,
      });

      return result;
    } catch (error: any) {
      console.error('Passkey authentication failed:', error);
      throw new Error(error.message || 'Passkey authentication failed');
    }
  }, [generateAuthenticationChallengeMutation, verifyAuthenticationMutation, setSession, clearSession, getPhoneFromUrl, getLastUserPhone, setLastUserPhone]);

  // Get current phone being used for authentication
  const getCurrentPhone = useCallback(() => {
    return getPhoneFromUrl() || getLastUserPhone();
  }, [getPhoneFromUrl, getLastUserPhone]);

  return {
    login,
    register,
    verifyCredentials,
    logout,
    refreshSession,
    loginWithPasskey,
    getCurrentPhone,
    getLastUserPhone,
    isLoading:
      signInMutation.isPending ||
      signUpMutation.isPending ||
      generateAuthenticationChallengeMutation.isPending ||
      verifyAuthenticationMutation.isPending,
    error:
      signInMutation.error ||
      signUpMutation.error ||
      generateAuthenticationChallengeMutation.error ||
      verifyAuthenticationMutation.error,
  };
}
